import React, { useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Popper,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface SearchBarProps {
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  searchPlaceholder: string;
  loading?: boolean;
  open?: boolean;
  onClose?: () => void;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
}

export default function SearchBar({
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder,
  loading = false,
  open = false,
  onClose,
  onFocus,
  onKeyDown,
  children,
}: SearchBarProps) {
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleClose();
    onSearchSubmit();
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        ref={anchorRef}
        component="form"
        onSubmit={handleSubmit}
        sx={{ flex: "1 1 auto", maxWidth: "600px", position: "relative" }}
      >
        <TextField
          fullWidth
          size="small"
          autoComplete="off"
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
          onFocus={handleFocus}
          onClick={handleFocus}
          onKeyDown={onKeyDown}
          sx={{
            backgroundColor: "background.paper",
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            },

            htmlInput: {
              role: "combobox",
              "aria-expanded": open,
              "aria-haspopup": "listbox",
              "aria-controls": open ? "product-list-results" : undefined,
              "aria-autocomplete": "list",
              "aria-label": searchPlaceholder,
            }
          }} />
        {children && anchorRef.current && (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{
              zIndex: 1300,
              minWidth: anchorRef.current.offsetWidth,
            }}
            modifiers={[
              {
                name: "preventOverflow",
                options: {
                  padding: 8,
                },
              },
            ]}
          >
            {children}
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
}
